import { Link } from 'react-router-dom';
import { Badge } from '@v2/design-system';
import { useI18n } from '@v2/providers/I18nProvider';
import ProductThumbnail from './ProductThumbnail';
import type { ProductListItem } from './types';

const formatBRL = (n: number | null) =>
    n == null ? '—' : n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const typeBadgeColor = (color: string | null): 'primary' | 'info' | 'success' | 'warning' | 'danger' | 'secondary' => {
    switch (color) {
        case 'primary':  return 'primary';
        case 'info':     return 'info';
        case 'success':  return 'success';
        case 'warning':  return 'warning';
        case 'danger':   return 'danger';
        default:         return 'secondary';
    }
};

function stockColor(qty: number): 'danger' | 'warning' | 'success' | 'secondary' {
    if (qty <= 0) return 'secondary';
    if (qty <= 5) return 'warning';
    return 'success';
}

function ColorChips({ colors }: { colors?: string[] }) {
    if (!colors?.length) {
        return <span className="text-xs text-gray-400">—</span>;
    }

    return (
        <div className="flex flex-wrap gap-1">
            {colors.map((color) => (
                <span
                    key={color}
                    className="inline-flex max-w-[5.5rem] truncate rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-medium text-gray-700 dark:bg-gray-700 dark:text-gray-200"
                    title={color}
                >
                    {color}
                </span>
            ))}
        </div>
    );
}

type Props = { product: ProductListItem };

export default function ProductListRow({ product }: Props) {
    const { t } = useI18n();
    const stockCd = product.stock_cd ?? product.stock_quantity;
    const stockTransit = product.stock_transit ?? 0;
    const stockReserved = product.stock_reserved ?? 0;

    return (
        <Link
            to={`/products/${product.id}`}
            className="flex w-full items-center gap-3 border-b border-gray-100 px-4 py-3 transition last:border-b-0 hover:bg-gray-50 dark:border-gray-700/50 dark:hover:bg-gray-800/60 lg:grid lg:grid-cols-[3rem_minmax(0,1.4fr)_7rem_6rem_5.5rem_5.5rem_5.5rem] lg:gap-3"
        >
            <ProductThumbnail src={product.image_url} alt={product.name} size="list" />

            <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-semibold text-gray-900 dark:text-gray-100">{product.name}</p>
                <p className="truncate text-xs text-gray-500 dark:text-gray-400">
                    {[product.code, product.supplier_code].filter(Boolean).join(' · ') || '—'}
                </p>
                <div className="mt-1 flex flex-wrap gap-1 lg:hidden">
                    {product.product_type_label && (
                        <Badge color={typeBadgeColor(product.product_type_color)} variant="flat" rounded="pill" size="sm">
                            {product.product_type_label}
                        </Badge>
                    )}
                    <Badge color={stockColor(stockCd)} variant="flat" rounded="pill" size="sm">
                        {t('v2.products.stock_cd_short')}: {stockCd}
                    </Badge>
                    {stockReserved > 0 && (
                        <Badge color="warning" variant="flat" rounded="pill" size="sm">
                            {t('v2.products.stock_reserved_short')}: {stockReserved}
                        </Badge>
                    )}
                    <Badge color={stockTransit > 0 ? 'info' : 'secondary'} variant="flat" rounded="pill" size="sm">
                        {t('v2.products.stock_transit_short')}: {stockTransit}
                    </Badge>
                </div>
            </div>

            <div className="hidden min-w-0 lg:block">
                <ColorChips colors={product.colors} />
            </div>

            <span className="hidden w-full truncate text-xs text-gray-600 dark:text-gray-400 md:block">
                {product.category ?? '—'}
            </span>

            <span className="hidden shrink-0 text-right text-sm font-semibold tabular-nums text-gray-900 dark:text-gray-100 lg:block">
                {formatBRL(product.unit_price)}
            </span>

            <span className="hidden text-center lg:block">
                <Badge color={stockColor(stockCd)} variant="flat" rounded="pill" size="sm">
                    {stockCd}
                </Badge>
                {stockReserved > 0 && (
                    <span className="mt-0.5 block text-[10px] font-medium text-amber-700 dark:text-amber-300" title={t('v2.products.stock_reserved_hint')}>
                        +{stockReserved} {t('v2.products.stock_reserved_short').toLowerCase()}
                    </span>
                )}
            </span>

            <span className="hidden text-center lg:block">
                <Badge color={stockTransit > 0 ? 'info' : 'secondary'} variant="flat" rounded="pill" size="sm">
                    {stockTransit}
                </Badge>
            </span>
        </Link>
    );
}
